// src/components/tutorials/JS_Array_Basic_Methods.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encoded = encodeURIComponent(code);
    window.open(`/editorJS?code=${encoded}`, "_blank");
  };
  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_Array_Basic_Methods() {
  const refs = {
    length: useRef(null),
    toString: useRef(null),
    at: useRef(null),
    join: useRef(null),
    pop: useRef(null),
    push: useRef(null),
    shift: useRef(null),
    unshift: useRef(null),
    delete: useRef(null),
    concat: useRef(null),
    copyWithin: useRef(null),
    flat: useRef(null),
    slice: useRef(null),
    splice: useRef(null),
    toSpliced: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const topics = [
    { key: "length", label: "Array length" },
    { key: "toString", label: "Array toString()" },
    { key: "at", label: "Array at()" },
    { key: "join", label: "Array join()" },
    { key: "pop", label: "Array pop()" },
    { key: "push", label: "Array push()" },
    { key: "shift", label: "Array shift()" },
    { key: "unshift", label: "Array unshift()" },
    { key: "delete", label: "Array delete()" },
    { key: "concat", label: "Array concat()" },
    { key: "copyWithin", label: "Array copyWithin()" },
    { key: "flat", label: "Array flat()" },
    { key: "slice", label: "Array slice()" },
    { key: "splice", label: "Array splice()" },
    { key: "toSpliced", label: "Array toSpliced()" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Array Basic Methods</h1>
      <p className="tutorial-intro">
        JavaScript provides many built-in methods to manipulate arrays easily.
        Let's explore the most commonly used ones.
      </p>

      {/* Key Topics */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.4rem", margin: 0 }}>
          Core Array Methods
        </h1>
        <p>Click on a topic to explore examples and try them interactively!</p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.3rem",
            marginTop: "1rem",
          }}
        >
          {topics.map((t) => (
            <button
              key={t.key}
              onClick={() => scrollTo(t.key)}
              style={{
                background: "transparent",
                border: "none",
                textAlign: "left",
                textDecoration: "underline",
                color: "#000",
                cursor: "pointer",
                fontSize: "1.05rem",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* --- Section Template Example --- */}
      {/* For each method below, same format: code + TryIt + Output */}

      {/* 1️⃣ length */}
      <div ref={refs.length} className="tutorial-section">
        <h2 className="section-title">1️⃣ Array length</h2>
        <p>
          The <code className="highlight_code">length</code> property returns
          the number of elements in an array.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.length);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.length);`}
          />
        </div>
        <OutputBox>
          <pre>3</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ toString() */}
      <div ref={refs.toString} className="tutorial-section">
        <h2 className="section-title">2️⃣ Array toString()</h2>
        <p>Converts the array to a comma-separated string.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.toString());`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.toString());`}
          />
        </div>
        <OutputBox>
          <pre>"Apple,Banana,Cherry"</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ at() */}
      <div ref={refs.at} className="tutorial-section">
        <h2 className="section-title">3️⃣ Array at()</h2>
        <p>
          The <code className="highlight_code">at()</code> method returns the
          element at a given index, supporting negative indexing.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.at(0));
console.log(fruits.at(-1));`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.at(0));
console.log(fruits.at(-1));`}
          />
        </div>
        <OutputBox>
          <pre>"Apple" "Cherry"</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ join() */}
      <div ref={refs.join} className="tutorial-section">
        <h2 className="section-title">4️⃣ Array join()</h2>
        <p>Joins all array elements into a string with a custom separator.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.join(" - "));`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.join(" - "));`}
          />
        </div>
        <OutputBox>
          <pre>"Apple - Banana - Cherry"</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ pop() */}
      <div ref={refs.pop} className="tutorial-section">
        <h2 className="section-title">5️⃣ Array pop()</h2>
        <p>Removes the last element and returns it.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.pop());
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.pop());
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>"Cherry" ["Apple", "Banana"]</pre>
        </OutputBox>
      </div>

      {/* 6️⃣ push() */}
      <div ref={refs.push} className="tutorial-section">
        <h2 className="section-title">6️⃣ Array push()</h2>
        <p>
          Adds new elements to the end of an array and returns the new length.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana"];
fruits.push("Cherry");
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana"];
fruits.push("Cherry");
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", "Banana", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 7️⃣ shift() */}
      <div ref={refs.shift} className="tutorial-section">
        <h2 className="section-title">7️⃣ Array shift()</h2>
        <p>Removes the first element and returns it.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.shift());
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.shift());
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>"Apple" ["Banana", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 8️⃣ unshift() */}
      <div ref={refs.unshift} className="tutorial-section">
        <h2 className="section-title">8️⃣ Array unshift()</h2>
        <p>Adds new elements to the beginning of an array.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Banana", "Cherry"];
fruits.unshift("Apple");
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Banana", "Cherry"];
fruits.unshift("Apple");
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", "Banana", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 9️⃣ delete */}
      <div ref={refs.delete} className="tutorial-section">
        <h2 className="section-title">9️⃣ Array delete</h2>
        <p>
          Deletes an element without changing the length (leaves an empty slot).
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
delete fruits[1];
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
delete fruits[1];
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", empty, "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 🔟 concat() */}
      <div ref={refs.concat} className="tutorial-section">
        <h2 className="section-title">🔟 Array concat()</h2>
        <p>Merges two or more arrays into a new one.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = ["A", "B"];
let b = ["C", "D"];
console.log(a.concat(b));`}
          />
          <TryItButton
            code={`let a = ["A", "B"];
let b = ["C", "D"];
console.log(a.concat(b));`}
          />
        </div>
        <OutputBox>
          <pre>["A", "B", "C", "D"]</pre>
        </OutputBox>
      </div>

      {/* 11️⃣ copyWithin() */}
      <div ref={refs.copyWithin} className="tutorial-section">
        <h2 className="section-title">11️⃣ Array copyWithin()</h2>
        <p>Copies array elements to another position in the same array.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let arr = ["A", "B", "C", "D"];
arr.copyWithin(2, 0, 2);
console.log(arr);`}
          />
          <TryItButton
            code={`let arr = ["A", "B", "C", "D"];
arr.copyWithin(2, 0, 2);
console.log(arr);`}
          />
        </div>
        <OutputBox>
          <pre>["A", "B", "A", "B"]</pre>
        </OutputBox>
      </div>

      {/* 12️⃣ flat() */}
      <div ref={refs.flat} className="tutorial-section">
        <h2 className="section-title">12️⃣ Array flat()</h2>
        <p>Flattens nested arrays into a single-level array.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let arr = [1, [2, [3, 4]]];
console.log(arr.flat(2));`}
          />
          <TryItButton
            code={`let arr = [1, [2, [3, 4]]];
console.log(arr.flat(2));`}
          />
        </div>
        <OutputBox>
          <pre>[1, 2, 3, 4]</pre>
        </OutputBox>
      </div>

      {/* 13️⃣ slice() */}
      <div ref={refs.slice} className="tutorial-section">
        <h2 className="section-title">13️⃣ Array slice()</h2>
        <p>Returns a portion of an array without modifying the original.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry", "Mango"];
console.log(fruits.slice(1, 3));`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry", "Mango"];
console.log(fruits.slice(1, 3));`}
          />
        </div>
        <OutputBox>
          <pre>["Banana", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 14️⃣ splice() */}
      <div ref={refs.splice} className="tutorial-section">
        <h2 className="section-title">14️⃣ Array splice()</h2>
        <p>Adds/removes elements at a specific position.</p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
fruits.splice(1, 1, "Mango");
console.log(fruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
fruits.splice(1, 1, "Mango");
console.log(fruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", "Mango", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* 15️⃣ toSpliced() */}
      <div ref={refs.toSpliced} className="tutorial-section">
        <h2 className="section-title">15️⃣ Array toSpliced()</h2>
        <p>
          <code className="highlight_code">toSpliced()</code> is like{" "}
          <code className="highlight_code">splice()</code>, but returns a new
          array without modifying the original.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
let newFruits = fruits.toSpliced(1, 1, "Mango");
console.log(fruits);
console.log(newFruits);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
let newFruits = fruits.toSpliced(1, 1, "Mango");
console.log(fruits);
console.log(newFruits);`}
          />
        </div>
        <OutputBox>
          <pre>["Apple", "Banana", "Cherry"] ["Apple", "Mango", "Cherry"]</pre>
        </OutputBox>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #bae6fd)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Practice chaining multiple array methods together.</li>
          <li>Explore advanced methods like map(), filter(), and reduce().</li>
          <li>Understand mutation vs non-mutation array methods.</li>
        </ul>
      </div>
    </div>
  );
}
